import { Component, ElementRef, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, Event, NavigationEnd, Router } from '@angular/router';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { AIAssistantService, AIChatMessage, AppContext, Chat, ChatAvailability, ChatList, LimitInfo, MessageList } from '../../services/ai-assistant.service';
import { SessionService } from '../../services/session.service';
import { tinykeys } from 'tinykeys';
import { ConfirmModalComponent } from '../../modals/confirm-modal/confirm-modal.component';
import { IConfirmModalData } from '../../modals/confirm-modal/models/confirmModalData';
import { IConfirmModalAnswer } from '../../modals/confirm-modal/models/confirmModalAnswer';
import { filter } from 'rxjs/operators';
import { PlatformApplications } from '../../models/platformApplications';

import { SHARED_MODULES } from '../../../shared.';
import { PhIconComponent } from '../ph-icon/ph-icon.component';
import { PhIconWrapperComponent } from '../ph-icon-wrapper/ph-icon-wrapper.component';
import { NgxSkeletonLoaderComponent } from 'ngx-skeleton-loader';
import { MarkdownComponent } from 'ngx-markdown';
import { PhButtonComponent } from '../ph-button/ph-button.component';

@Component({
    selector: 'app-ai-assistant',
    templateUrl: './ai-assistant.component.html',
    styleUrls: ['./ai-assistant.component.scss'],
    host: { '[class.minimized]': 'minimized' },
	imports: [
		SHARED_MODULES,
		MarkdownComponent,
		NgxSkeletonLoaderComponent,
		PhButtonComponent,
		PhIconComponent,
		PhIconWrapperComponent
	],
    standalone: true
})
export class AIAssistantComponent implements OnInit, OnDestroy {

	public activeChat: { chat: Chat, messages: Array<AIChatMessage> } = { chat: null, messages: [] };
	public agreed: boolean = false;
	public chats: Array<Chat> = [];
	public confirmed: boolean = false;
	public contactLink: string = '';
	public currentPageButtonActive: boolean = false;
	public error: boolean = false;
	private getChatTimeout: any = null;
	public isAdmin: boolean = false;
	public limitInfo: LimitInfo = null;
	public loadingChat: boolean = false;
	public minimized: boolean = true;
	public overloaded: boolean = false;
	public presets: Array<any> = [];
	public privacyPolicyLink: string;
	public showCurrentPageButton: boolean = false;
	public waitingMessage: boolean = false;
	public waitingMessageText: string = 'AI_ASSISTANT.WAIT_ANSWER';

	private tinykeysSubscription: () => void;

	@Output() close = new EventEmitter<void>();
	@ViewChild('chat') chat: ElementRef;
	@ViewChild('input') input: ElementRef;

	constructor(
		private dialog: MatDialog,
		private route: ActivatedRoute,
		private router: Router,
		private translate: TranslateService,
		private AIAssistantService: AIAssistantService,
		private sessionService: SessionService
	) {
		this.isAdmin = this.sessionService.defaults.isAdmin;
		if (this.isAdmin) {
			this.contactLink = this.sessionService.getExternalLink(
				PlatformApplications.AdminPanel,
				'/wizora-activity?wizora_requests_form=true'
			);
		}
		this.privacyPolicyLink = this.sessionService.media.terms_use_wizora?.url;
		this.presets.push(
			{ icon: 'entityStores', color: '#11a75c', title: 'AI_ASSISTANT.PRESETS.STORE_PROFIT_TITLE', instructions: 'AI_ASSISTANT.PRESETS.STORE_PROFIT_INSTRUCTIONS' },
			{ icon: 'layout', color: '#722ed1', title: 'AI_ASSISTANT.PRESETS.TOP_PLANOGRAM_TITLE', instructions: 'AI_ASSISTANT.PRESETS.TOP_PLANOGRAM_INSTRUCTIONS' },
			{ icon: 'cart', color: '#ffa73f', title: 'AI_ASSISTANT.PRESETS.POPULAR_PRODUCTS_TITLE', instructions: 'AI_ASSISTANT.PRESETS.POPULAR_PRODUCTS_INSTRUCTIONS' },
			{ icon: 'barChart', color: '#4a72ff', title: 'AI_ASSISTANT.PRESETS.SUPPLIERS_RATING_TITLE', instructions: 'AI_ASSISTANT.PRESETS.SUPPLIERS_RATING_INSTRUCTIONS' },
			{ icon: 'sortV', color: '#ff4d4f', title: 'AI_ASSISTANT.PRESETS.WORST_BRANDS_TITLE', instructions: 'AI_ASSISTANT.PRESETS.WORST_BRANDS_INSTRUCTIONS' },
			{ icon: 'book', color: '#212121', title: 'AI_ASSISTANT.PRESETS.GUIDE_TITLE', instructions: 'AI_ASSISTANT.PRESETS.GUIDE_INSTRUCTIONS' }
		);

		this.tinykeysSubscription = tinykeys(window, {
			'Enter': (event: KeyboardEvent) => {
				event.preventDefault();

				if (this.input && this.input.nativeElement.value.trim()) this.sendMessage(this.input.nativeElement.value);
			},
			'$mod+Enter': (event: KeyboardEvent) => {
				event.preventDefault();

				if (this.input) {
					this.input.nativeElement.value += '\r';
					this.autoResize(this.input.nativeElement);
					this.input.nativeElement.scrollTop = this.input.nativeElement.scrollHeight;
				}
			},
			'Shift+Enter': (event: KeyboardEvent) => {
				event.preventDefault();

				if (this.input) {
					this.input.nativeElement.value += '\r';
					this.autoResize(this.input.nativeElement);
					this.input.nativeElement.scrollTop = this.input.nativeElement.scrollHeight;
				}
			}
		});
		this.checkCurrentPageButton();
		this.router.events
			.pipe(filter((event: Event) => event instanceof NavigationEnd))
			.subscribe((_: Event) => this.checkCurrentPageButton());
	}

	public ngOnInit(): void {
		this.limitInfo = this.AIAssistantService.limitInfo;
		this.confirmed = this.AIAssistantService.privacyPolicyAccepted;
		this.currentPageButtonActive = this.AIAssistantService.activeCurrentPageButton && this.showCurrentPageButton;
		setTimeout(() => {
			if (this.input) this.input.nativeElement.value = this.AIAssistantService.activeMessage;
		});

		if (this.confirmed) this.getChatList();
	}

	public agree(): void {
		this.AIAssistantService.confirmPrivacyPolicy().subscribe((value: { status: boolean }) => {
			this.confirmed = this.AIAssistantService.privacyPolicyAccepted = value.status;

			if (this.confirmed) this.getChatList();
		});
	}

	public autoResize(textarea: HTMLElement): void {
		textarea.style.height = 'auto';
		textarea.style.height = textarea.scrollHeight + 'px';
	}

	public changeReaction(message: AIChatMessage, reaction: boolean): void {
		this.AIAssistantService
			.changeReaction(this.activeChat.chat.id, message.id, reaction === message.liked ? null : reaction)
			.subscribe((result: { liked: boolean }) => {
				message.liked = result.liked;
			});
	}

	private checkCurrentPageButton() {
		const urls: Array<string> = ['stand/edit', 'stand/view', 'plan/edit', 'plan/view'];
		this.showCurrentPageButton = urls.some((url: string) => this.router.url.includes(url));
		if (!this.showCurrentPageButton) this.currentPageButtonActive = false;
	}

	public clearMessages(): void {
		this.activeChat.chat = null;
		this.activeChat.messages.length = 0;
		this.waitingMessage = false;
		if (this.input) this.input.nativeElement.value = '';
		if (this.getChatTimeout) {
			clearTimeout(this.getChatTimeout);
			this.getChatTimeout = null;
		}
	}

	public createChat(message: string): void {
		this.AIAssistantService.createChat(message).subscribe({
			next: (chat: Chat) => {
				this.chats.unshift(chat);
				this.activeChat.chat = chat;
				this.sendMessageRequest(chat.id, message);
			},
			error: (error) => {
				this.error = true;
				setTimeout(() => this.error = false, 1000 * 10);
				this.clearMessages();
			}
		});
	}

	public deleteChat(chat: Chat): void {
		this.dialog
			.open<ConfirmModalComponent, IConfirmModalData, IConfirmModalAnswer>(ConfirmModalComponent, {
				width: '574px',
				autoFocus: false,
				backdropClass: 'back-drop',
				data: {
					label: 'AI_ASSISTANT.DELETE_CHAT_CONFIRM',
					labelTranslateParams: { title: chat.title },
					confirmLabel: 'YES',
					cancelLabel: 'NO',
					twoAnswers: true
				}
			})
			.afterClosed()
			.subscribe((result: IConfirmModalAnswer) => {
				if (!result.answer) return;

				if (this.activeChat.chat?.id == chat.id) this.clearMessages();
				const index: number = this.chats.findIndex((item) => item.id === chat.id);
				this.chats.splice(index, 1);

				this.AIAssistantService.deleteChat(chat.id).subscribe();
			});
	}

	public getChat(id: number): void {
		this.AIAssistantService.getChat(id).subscribe((chat: Chat) => {
			if (chat.wait_answer) {
				this.getChatTimeout = setTimeout(() => this.getChat(id), 1000 * 5);
			} else {
				this.getChatTimeout = null;
				this.getMessages(chat.id);
			}
		});
	}

	public getChatList(): void {
		if (this.AIAssistantService.activeChatId) this.loadingChat = true;
		this.AIAssistantService.getChatList().subscribe((list: ChatList) => {
			this.chats = list.results;
			if (this.AIAssistantService.activeChatId) this.loadChat(this.AIAssistantService.activeChatId);
		});
	}

	private getLimitInfo(): void {
		this.AIAssistantService.getAvailability().subscribe((result: ChatAvailability) => {
			if (result.limit_info) {
				this.limitInfo = result.limit_info;
				this.AIAssistantService.limitInfo = result.limit_info;
			}
		});
	}

	public getMessages(chatId: number): void {
		this.AIAssistantService.getMessages(chatId).subscribe({
			next: (list: MessageList) => {
				this.activeChat.messages = list.results.reverse();
				this.activeChat.chat = this.chats.find((chat: Chat) => chat.id === chatId);
				this.loadingChat = false;
				this.waitingMessage = false;
				this.scrollChat();

				if (list.wait_answer) {
					this.waitingMessage = true;
					this.getChat(chatId);
				}
			},
			error: (err) => console.error(err)
		});
	}

	public loadChat(chatId: number): void {
		this.clearMessages();
		this.loadingChat = true;
		this.getMessages(chatId);
	}

	public onMarkdownReady(element: HTMLElement) {
		const links = element.querySelectorAll('a');
		links.forEach(link => link.setAttribute('target', '_blank'));
	}

	private scrollChat(): void {
		setTimeout(() => {
			if (this.chat) this.chat.nativeElement.scrollTop = this.chat.nativeElement.scrollHeight;
		});
	}

	public sendMessage(message: string): void {
		if (this.waitingMessage || this.limitInfo?.overused) return;
		if (this.input) this.input.nativeElement.value = '';

		this.activeChat.messages.push({
			author: 'user',
			content: {
				role: 'user',
				parts: [{ text: message }]
			},
			created_at: Date.now().toString(),
			id: null,
			liked: null
		});
		this.scrollChat();
		this.waitingMessage = true;
		this.waitingMessageText = 'AI_ASSISTANT.WAIT_ANSWER';

		if (this.activeChat.chat) {
			const currentChat: number = this.activeChat.chat.id;
			this.sendMessageRequest(currentChat, message);
		} else {
			this.createChat(message);
		}
	}

	public sendMessageRequest(chat: number, message: string): void {
		const context: Partial<AppContext> = {};

		if (this.currentPageButtonActive && (this.router.url.includes('stand/edit') || this.router.url.includes('stand/view'))) {
			let currentRoute: ActivatedRouteSnapshot = this.route.snapshot.firstChild;
			while (currentRoute.firstChild) {
				currentRoute = currentRoute.firstChild;
			}
			context.planogram_id = currentRoute.params.id;
			context.plan_id = currentRoute.queryParams.planogram;
		}

		if (this.currentPageButtonActive && (this.router.url.includes('plan/edit') || this.router.url.includes('plan/view'))) {
			let currentRoute: ActivatedRouteSnapshot = this.route.snapshot.firstChild;
			while (currentRoute.firstChild) {
				currentRoute = currentRoute.firstChild;
			}
			context.plan_id = currentRoute.params.id;
		}

		this.AIAssistantService.sendMessage(chat, message, context).subscribe({
			next: (event: any) => {
				if (!event) return;

				if (event.error) {
					this.error = true;
					setTimeout(() => this.error = false, 1000 * 10);
					return;
				}

				if (event.overloaded) {
					this.overloaded = true;
					setTimeout(() => this.overloaded = false, 1000 * 10);
					return;
				}

				if (event.limit_info) {
					this.limitInfo = event.limit_info;
					this.AIAssistantService.limitInfo = event.limit_info;
					return;
				}

				if (this.activeChat.chat?.id == chat && event.partial) {
					this.waitingMessageText = event.parts[0].text || 'AI_ASSISTANT.WAIT_ANSWER';
				}

				if (this.activeChat.chat?.id == chat && !event.partial) {
					this.waitingMessageText = 'AI_ASSISTANT.WAIT_ANSWER';
					this.activeChat.messages.push({
						author: 'db_multiagent',
						content: {
							role: event.role,
							parts: event.parts
						},
						created_at: Date.now().toString(),
						id: event.id,
						liked: null
					});
				}

				this.scrollChat();
			},
			error: (err) => console.error(err),
			complete: () => {
				this.waitingMessage = false;
				this.getLimitInfo();
			},
		});
	}

	public usePreset(key: string): void {
		this.sendMessage(this.translate.instant(key));
	}

	public ngOnDestroy(): void {
		if (this.tinykeysSubscription) this.tinykeysSubscription();
		this.AIAssistantService.activeChatId = this.activeChat.chat?.id || null;
		this.AIAssistantService.activeCurrentPageButton = this.currentPageButtonActive;
		if (this.input) {
			this.AIAssistantService.activeMessage = this.input.nativeElement.value;
		}
		if (this.getChatTimeout) {
			clearTimeout(this.getChatTimeout);
			this.getChatTimeout = null;
		}
	}
}